import { formatARS, PRICE_ROUNDING_STEPS, type RoundingMode } from '@gl/shared';
import { ArrowDown, ArrowDownUp, ArrowUp, Ban } from 'lucide-react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/ui/input';
import { ChipGroup, OptionCards } from '../ui/option-cards';
import type { WizardValues } from '../wizard-form';
import { exampleText } from './OperationStep';

export const ROUNDING_LABELS: Record<RoundingMode, string> = {
  NONE: 'Sin redondeo',
  NEAREST: 'Al más cercano',
  UP: 'Para arriba',
  DOWN: 'Para abajo',
};

export function RoundingStep({ form, sampleCents }: { form: UseFormReturn<WizardValues>; sampleCents: number }) {
  const values = useWatch({ control: form.control }) as WizardValues;
  const { errors } = form.formState;
  const example = exampleText(values, sampleCents, true);

  return (
    <div className="grid gap-5">
      <Controller
        control={form.control}
        name="roundingMode"
        render={({ field }) => (
          <OptionCards
            name="Redondeo"
            className="sm:grid-cols-2 lg:grid-cols-4"
            value={field.value}
            onChange={field.onChange}
            options={[
              { value: 'NONE', label: ROUNDING_LABELS.NONE, description: 'Queda el resultado exacto', icon: <Ban /> },
              { value: 'NEAREST', label: ROUNDING_LABELS.NEAREST, description: '$ 7.475 → $ 7.500', icon: <ArrowDownUp /> },
              { value: 'UP', label: ROUNDING_LABELS.UP, description: '$ 7.420 → $ 7.500', icon: <ArrowUp /> },
              { value: 'DOWN', label: ROUNDING_LABELS.DOWN, description: '$ 7.480 → $ 7.400', icon: <ArrowDown /> },
            ]}
          />
        )}
      />

      {values.roundingMode !== 'NONE' && (
        <FormField label="Redondear a múltiplos de" error={errors.stepCents?.message}>
          <Controller
            control={form.control}
            name="stepCents"
            render={({ field }) => (
              <ChipGroup
                name="Paso de redondeo"
                value={field.value}
                onChange={field.onChange}
                options={PRICE_ROUNDING_STEPS.map((s) => ({ value: s, label: formatARS(s) }))}
              />
            )}
          />
        </FormField>
      )}

      {example && <p className="rounded-md bg-muted px-3 py-2 text-sm">{example}</p>}

      <FormField label="Nota (opcional)" htmlFor="price-note" error={errors.note?.message} hint="Queda en el historial, p. ej. “Aumento de octubre”">
        <Input id="price-note" maxLength={200} placeholder="Aumento de octubre" {...form.register('note')} />
      </FormField>
    </div>
  );
}
